import { useCallback } from "react"
import {
  SlideElement,
  TextSlideElementProps,
  ImageSlideElementProps,
  TableSlideElementProps,
  ChartSlideElementProps,
} from "types/ReportingDashboard/elements.types"
import { Coordinates } from "./useElementConstraints"

/**
 * Element update properties - using proper discriminated union based on element type
 */
export type ElementUpdateProperties =
  | Partial<TextSlideElementProps>
  | Partial<ImageSlideElementProps>
  | Partial<TableSlideElementProps>
  | Partial<ChartSlideElementProps>
  | {
      // Common properties that can be updated on any element
      x?: number
      y?: number
      width?: number
      height?: number
    }

/**
 * Return type for useElementOperations hook
 */
export interface ElementOperationsResult {
  handleElementUpdate: (
    id: string | number,
    updatedProperties: ElementUpdateProperties
  ) => void
  isTemplateElement: (element: SlideElement) => boolean
}

/**
 * Custom hook for managing element operations and template logic.
 *
 * Handles updates to canvas elements while respecting template mode restrictions.
 * Provides utilities for identifying template elements and updating their properties
 * with proper constraint application. Uses proper TypeScript discriminated unions
 * for type-safe element property updates.
 *
 * @param elements - Array of canvas elements
 * @param onElementsChange - Callback to update the elements array
 * @param isTemplateMode - Whether the canvas is in template editing mode
 * @param constrainToBoundaries - Function to apply position constraints
 *
 * @returns Element operation functions
 *
 * @example
 * const { handleElementUpdate, isTemplateElement } = useElementOperations(
 *   elements, setElements, isTemplateMode, constrainToBoundaries
 * )
 *
 * // Update element position (works on any element)
 * handleElementUpdate('element-1', { x: 100, y: 200 })
 *
 * // Update text element properties (type-safe)
 * handleElementUpdate('text-1', {
 *   text: 'New content',
 *   fontSize: 18,
 *   isBold: true,
 *   textAlign: TEXT_ALIGN.CENTER
 * })
 *
 * // Update image element properties (type-safe)
 * handleElementUpdate('image-1', {
 *   imageSrc: 'https://example.com/image.jpg',
 *   width: 300,
 *   height: 200
 * })
 *
 * // Check if element is from template
 * const isTemplate = isTemplateElement(element)
 */
export const useElementOperations = (
  elements: SlideElement[],
  onElementsChange: (elements: SlideElement[]) => void,
  isTemplateMode: boolean,
  constrainToBoundaries: (
    id: string | number,
    x: number,
    y: number
  ) => Coordinates
): ElementOperationsResult => {
  /**
   * Determines if an element belongs to the template layer.
   *
   * @param element - The element to check
   * @returns True if element is a template element
   */
  const isTemplateElement = useCallback(
    (element: SlideElement): boolean => element?.isTemplateElement === true,
    []
  )

  /**
   * Updates properties of a canvas element with constraint validation.
   *
   * Prevents updating template elements when not in template mode.
   * Automatically applies position constraints when x/y coordinates are updated.
   * Uses proper TypeScript discriminated unions for type-safe property updates.
   *
   * @param id - ID of the element to update
   * @param updatedProperties - Object containing properties to update (type-safe based on element type)
   *
   * @example
   * // Update element position (common to all elements)
   * handleElementUpdate('text-1', { x: 100, y: 200 })
   *
   * // Update text element content and styling (type-safe)
   * handleElementUpdate('text-1', {
   *   text: 'New content',
   *   fontSize: 18,
   *   isBold: true,
   *   textAlign: TEXT_ALIGN.CENTER
   * })
   *
   * // Update image element (type-safe)
   * handleElementUpdate('image-1', {
   *   imageSrc: 'new-image.jpg',
   *   width: 400
   * })
   */
  const handleElementUpdate = useCallback(
    (id: string | number, updatedProperties: ElementUpdateProperties): void => {
      const elementIndex = elements.findIndex(
        (el) => String(el.id) === String(id)
      )
      if (elementIndex === -1) return

      const element = elements[elementIndex]

      // Prevent updating template elements when not in template mode
      if (!isTemplateMode && isTemplateElement(element)) {
        console.warn(
          "Attempted to update a template element outside template mode."
        )
        return
      }

      const finalUpdates = { ...updatedProperties }

      // Handle position constraints for x/y coordinate updates
      if ("x" in finalUpdates || "y" in finalUpdates) {
        const currentX =
          "x" in finalUpdates && finalUpdates.x !== undefined
            ? finalUpdates.x
            : element.x
        const currentY =
          "y" in finalUpdates && finalUpdates.y !== undefined
            ? finalUpdates.y
            : element.y

        const { x: constrainedX, y: constrainedY } = constrainToBoundaries(
          id,
          currentX,
          currentY
        )

        // Only update if position actually changed after constraints
        if (constrainedX !== element.x) {
          finalUpdates.x = constrainedX
        } else {
          delete finalUpdates.x
        }

        if (constrainedY !== element.y) {
          finalUpdates.y = constrainedY
        } else {
          delete finalUpdates.y
        }
      }

      // Create the updated element with proper typing
      const updatedElement: SlideElement = {
        ...element,
        ...finalUpdates,
      } as SlideElement

      // Create new elements array with the updated element
      const newElements: SlideElement[] = [
        ...elements.slice(0, elementIndex),
        updatedElement,
        ...elements.slice(elementIndex + 1),
      ]

      onElementsChange(newElements)
    },
    [
      elements,
      isTemplateMode,
      isTemplateElement,
      constrainToBoundaries,
      onElementsChange,
    ]
  )

  return { handleElementUpdate, isTemplateElement }
}
